<script setup>
/**
 * FormControlUsersGroupsRolesGroup
 * Renders a table matrix of permissions for a specific Roles Group.
 * * Features:
 * - Row-level bulk toggling via native DOM manipulation of child checkboxes.
 * - Dynamic filtering of components based on the rolesGroup prop.
 */
import {computed} from 'vue'
import {useComponentsStore} from '@/stores/components'
import {useUserRolesStore} from '@/stores/userRoles' // Adjust path to your userRoles store
import FormControlUsersGroupsRolesCheckbox from './FormControlUsersGroupsRolesCheckbox.vue'

const props = defineProps({
    formId: {
        type: String,
        required: true
    },
    rolesGroup: {
        type: String,
        required: true
    },
})

// Pinia Stores
const componentsStore = useComponentsStore()
const userRolesStore = useUserRolesStore()

// --- Computed ---

const options = computed(() => userRolesStore.options)

const groupedComponents = computed(() => {
    return (componentsStore.items || []).filter(obj =>
        obj.roles && obj.rolesGroup === props.rolesGroup
    )
})

// --- Methods ---

/**
 * Toggles all checkboxes in a specific row.
 * Logic:
 * 1. Find the parent row of the clicked header.
 * 2. Find all currently unchecked boxes.
 * 3. If everything is checked, target everything to uncheck it.
 * 4. Trigger native .click() to ensure child component logic remains in sync.
 */
const toggleAllCheckboxes = (e) => {
    const row = e.target.closest('tr')
    if (!row) return

    let checkboxes = row.querySelectorAll('input[type="checkbox"]:not(:checked)')

    if (checkboxes.length === 0) {
        checkboxes = row.querySelectorAll('input[type="checkbox"]')
    }

    checkboxes.forEach(checkbox => checkbox.click())
}
</script>

<template>
    <table class="table table-condensed">
        <thead>
        <tr>
            <th class="col-xs-7"></th>
            <th v-for="option in options" :key="option.id" class="col-xs-1">
                {{ option.title }}
            </th>
        </tr>
        </thead>

        <tbody>
        <tr v-for="component in groupedComponents" :key="component.id">
            <th
                class="roles-component"
                style="cursor: pointer; user-select: none;"
                @click="toggleAllCheckboxes"
            >
                {{ component.title }}
            </th>

            <td v-for="option in options" :key="option.id">
                <FormControlUsersGroupsRolesCheckbox
                    :component="component"
                    :form-id="formId"
                    :option="option"
                    :roles-group="rolesGroup"
                />
            </td>
        </tr>
        </tbody>
    </table>
</template>

<style scoped>
.roles-component:hover {
    text-decoration: underline;
    color: var(--bs-primary);
}
</style>